import { allPass, isEmptyish, isString } from 'remeda'
import type { JSONSerializable } from './cache'
import { getCachedJsonString } from './cache'

// AG Grid cell parameter types
type AGGridCellParams = {
  readonly node: {
    setDataValue: (columnId: string, value: unknown) => void
  }
  readonly column: {
    getColId: () => string
  }
  readonly oldValue: unknown
}

// Generic validation function type
type ValidationPredicate<T = unknown> = (value: unknown) => value is T

/**
 * Value Comparison - pure function using cached JSON strings
 * Efficient comparison for complex objects using serialization
 */
export const hasValueChanged = (
  newValue: JSONSerializable,
  currentValue: JSONSerializable
): boolean => {
  // Handle the union type properly with type guards
  const getStringValue = (value: JSONSerializable): string => {
    if (
      value === null ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return getCachedJsonString(value)
    }
    // TypeScript now knows this is an object or array
    return getCachedJsonString(value as object)
  }

  return getStringValue(newValue) !== getStringValue(currentValue)
}

/**
 * Common validation patterns using remeda guards and allPass
 * Type predicates that narrow unknown types safely
 */
export const isValidString: ValidationPredicate<string> = (
  value: unknown
): value is string => isString(value) && !isEmptyish(value)

/**
 * Generic validator factory - creates composite validation functions
 * Eliminates verbose type assertions and promotes reusability
 */
export const createValidator = <T = unknown>(
  ...predicates: ValidationPredicate[]
): ValidationPredicate<T> => allPass(predicates) as ValidationPredicate<T>

/**
 * Normalize values for consistent type comparison
 *
 * Converts string numbers to actual numbers for proper comparison:
 * - "123" vs 123 → both become 123 (equal)
 * - "123.45" vs 123.45 → both become 123.45 (equal)
 *
 * Edge cases handled safely:
 * - "" (empty string) → returns as-is (not converted to 0)
 * - null/undefined → returns as-is (not converted to 0)
 * - boolean → returns as-is (not converted to 0/1)
 *
 * This ensures only actual numeric strings are normalized, preventing
 * false positives like comparing "" with null as both being 0.
 */
export const normalizeForComparison = (
  value1: unknown,
  value2: unknown
): readonly [unknown, unknown] => {
  // Only convert if both values are strings or numbers (not null, undefined, boolean, etc.)
  const isConvertible = (v: unknown): boolean =>
    typeof v === 'number' || (typeof v === 'string' && v.trim() !== '')

  if (!(isConvertible(value1) && isConvertible(value2))) {
    return [value1, value2] as const
  }

  // Number() handles both integers and floats correctly: "123" → 123, "123.45" → 123.45
  const num1 = Number(value1)
  const num2 = Number(value2)

  // Check if both values successfully converted to valid numbers
  const bothAreNumbers = !(Number.isNaN(num1) || Number.isNaN(num2))

  return bothAreNumbers ? ([num1, num2] as const) : ([value1, value2] as const)
}

// Export types for external use
export type { AGGridCellParams, ValidationPredicate }
